'use client'

import { updatePrivacySettingsAction } from '@/app/actions/lgpd'
import { createBrowserClient } from '@vagahub/db/browser'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { HIDDEN_PREFIXES } from '../layout/MobileBottomBar'

const CONSENT_KEY = 'cookie_consent'

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const posthog = usePostHog()
  const pathname = usePathname()
  // Achado do PLAN-ui-acessibilidade-mobile.md item 2: banner colidia com a
  // MobileBottomBar em mobile (as duas fixas em bottom-0). Sobe o banner só
  // nas rotas onde a bottom bar de fato aparece — nas outras (auth, redirect,
  // admin) bottom-0 já é o lugar certo, sem espaço vazio artificial.
  const bottomBarVisible = !!pathname && !HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (!stored) {
      setIsVisible(true)
    }
  }, [])

  // Sincroniza a escolha com o perfil + privacy_consent_logs (auditoria LGPD)
  // quando o usuário está logado. Fire-and-forget e best-effort: usuários
  // anônimos continuam cobertos só pelo localStorage acima.
  const syncToDb = async (analyticsOptOut: boolean) => {
    try {
      const supabase = createBrowserClient()
      const { data } = await supabase.auth.getUser()
      if (data.user) {
        await updatePrivacySettingsAction(analyticsOptOut, true)
      }
    } catch {
      // Non-blocking: consentimento local já foi aplicado independentemente do sync.
    }
  }

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    posthog?.opt_in_capturing()
    setIsVisible(false)
    syncToDb(false)
  }

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected')
    posthog?.opt_out_capturing()
    setIsVisible(false)
    syncToDb(true)
  }

  if (!isVisible) return null

  return (
    <dialog
      open
      aria-label="Aviso de cookies"
      className={`fixed ${bottomBarVisible ? 'bottom-14 md:bottom-0' : 'bottom-0'} left-0 right-0 z-50 m-0 flex w-full justify-center border-none bg-transparent px-4 pb-4 animate-in slide-in-from-bottom-4 fade-in duration-300 max-w-none p-0`}
    >
      <div className="w-full max-w-2xl rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-2xl p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <p className="flex-1 text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
          Usamos cookies pra melhorar sua experiência e analisar tráfego. Você pode aceitar ou
          recusar — sua escolha não bloqueia o uso do site.{' '}
          <Link
            href="/legal/politica-de-privacidade"
            className="underline underline-offset-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            Política de Privacidade
          </Link>
        </p>

        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={handleReject}
            className="h-11 rounded-lg px-4 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Recusar
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="h-11 rounded-lg px-4 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            Aceitar
          </button>
        </div>
      </div>
    </dialog>
  )
}

export default CookieConsentBanner
